import React from 'react';
import {Link} from 'react-router-dom';
import {Ban, ChevronRight, Clock, Contact, Drama, Eye, Gavel, HelpCircle, Images, Lightbulb, Palette, PartyPopper, ScrollText, Swords, Users, VenetianMask} from 'lucide-react';
import './Games.css';

interface GameDef {
    to: string;
    icon: React.ReactNode;
    accent: string;
    title: string;
    desc: string;
    players: string;
    duration: string;
}

const GAMES: GameDef[] = [
    {
        to: '/games/spy',
        icon: <Eye size={22}/>,
        accent: '#3d9dff',
        title: 'Шпион',
        desc: 'Все игроки узнают локацию, кроме шпиона. Его задача — вычислить место по разговору, не выдав себя.',
        players: '3+',
        duration: '10–15 мин',
    },
    {
        to: '/games/undercover',
        icon: <VenetianMask size={22}/>,
        accent: '#d946ef',
        title: 'Неизвестный шпион',
        desc: 'У одного или нескольких игроков ситуация слегка отличается, но сами они об этом не знают.',
        players: '3+',
        duration: '10–15 мин',
    },
    {
        to: '/games/charades',
        icon: <Drama size={22}/>,
        accent: '#2ecc71',
        title: 'Крокодил / Активити',
        desc: 'По очереди каждый объясняет слово жестами или рисунком, не произнося его вслух.',
        players: '2+',
        duration: '15–20 мин',
    },
    {
        to: '/games/taboo',
        icon: <Ban size={22}/>,
        accent: '#ff2d55',
        title: 'Табу',
        desc: 'Объясни слово словами, не называя его и ещё четыре подсказки-паразита из списка.',
        players: '2+',
        duration: '15–20 мин',
    },
    {
        to: '/games/riddles',
        icon: <HelpCircle size={22}/>,
        accent: '#ffb020',
        title: 'Данетки',
        desc: 'Ведущий втайне узнаёт загадку с разгадкой, а остальные раскрывают её вопросами «да / нет / не важно».',
        players: '3+',
        duration: '10–20 мин',
    },
    {
        to: '/games/who-am-i',
        icon: <Contact size={22}/>,
        accent: '#14b8a6',
        title: 'Кто я?',
        desc: 'Имя видно всем, кроме тебя — держи телефон экраном к остальным, они подсказывают, не называя имя.',
        players: '3+',
        duration: '10–15 мин',
    },
    {
        to: '/games/associations',
        icon: <Lightbulb size={22}/>,
        accent: '#84cc16',
        title: 'Ассоциации',
        desc: 'Каждый втайне придумывает подсказку-слово к общему слову. Одинаковые вычёркиваются — отгадывающий видит только уникальные.',
        players: '3+',
        duration: '15–20 мин',
    },
    {
        to: '/games/telephone',
        icon: <Palette size={22}/>,
        accent: '#f97316',
        title: 'Испорченный телефон',
        desc: 'Рисуешь слово пальцем на экране, следующий видит только рисунок и пишет догадку, следующий рисует её — и так по кругу.',
        players: '3+',
        duration: '10–20 мин',
    },
    {
        to: '/games/story-chain',
        icon: <ScrollText size={22}/>,
        accent: '#38bdf8',
        title: 'Фанты-история',
        desc: 'По кругу каждый добавляет одно предложение к истории, видя только предыдущее. В финале — абсурдный рассказ целиком.',
        players: '3+',
        duration: '10–15 мин',
    },
    {
        to: '/games/fibbage',
        icon: <Gavel size={22}/>,
        accent: '#64748b',
        title: 'Правда или подстава',
        desc: 'Настоящий ответ спрятан — каждый придумывает правдоподобный фейк, потом все голосуют, где правда.',
        players: '3+',
        duration: '15–25 мин',
    },
    {
        to: '/games/caption-draw',
        icon: <Images size={22}/>,
        accent: '#eab308',
        title: 'Подпиши рисунок',
        desc: 'Художник тайно рисует фразу, остальные придумывают свои подписи к рисунку — угадайте, где настоящая.',
        players: '3+',
        duration: '15–25 мин',
    },
    {
        to: '/games/caption-battle',
        icon: <Swords size={22}/>,
        accent: '#f43f5e',
        title: 'Битва подписей',
        desc: 'Смешной вопрос — каждый пишет свой ответ втайне, потом все голосуют за самый смешной.',
        players: '3+',
        duration: '10–20 мин',
    },
];

const GamesHub: React.FC = () => {
    return (
        <div className="GmPage GmPage--noBar">
            <div className="GmContainer">
                <div className="GmHubHeader">
                    <div className="GmHubHeader-icon"><PartyPopper size={26}/></div>
                    <div>
                        <h1 className="GmTitle">Игры для компании</h1>
                        <p className="GmSubtitle">
                            Играем на одном телефоне — передаём его по кругу, каждый смотрит свою карточку так,
                            чтобы не видели остальные.
                        </p>
                    </div>
                </div>

                <div className="GmHubGrid">
                    {GAMES.map(game => (
                        <Link
                            to={game.to}
                            className="GmHubCard"
                            key={game.to}
                            style={{'--gm-accent': game.accent} as React.CSSProperties}
                        >
                            <div className="GmHubCard-icon">{game.icon}</div>
                            <div className="GmHubCard-body">
                                <p className="GmHubCard-title">{game.title}</p>
                                <p className="GmHubCard-desc">{game.desc}</p>
                                <div className="GmHubCard-meta">
                                    <span className="GmHubCard-metaItem"><Users size={13}/>{game.players}</span>
                                    <span className="GmHubCard-metaItem"><Clock size={13}/>{game.duration}</span>
                                </div>
                            </div>
                            <ChevronRight className="GmHubCard-chevron" size={20}/>
                        </Link>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default GamesHub;
